import { Version, FeatureRequest, BugReport, Stage } from "../types";

// In-memory store — replace with DynamoDB API calls later
let versions: Version[] = [
  {
    id: "v-001",
    version: "0.1.0",
    stage: "dev",
    status: "deployed",
    timestamp: "2026-06-12T22:00:00Z",
    commitHash: "a1b2c3d",
    commitMessage: "Initial POS system scaffold with checkout flow",
    linkedFeatures: [],
    linkedBugs: [],
  },
];

let featureRequests: FeatureRequest[] = [
  {
    id: "fr-001",
    title: "Product catalog with barcode scanning",
    description: "Allow cashiers to scan product barcodes and auto-populate item details in the cart",
    status: "open",
    priority: "high",
    createdAt: "2026-06-12T20:00:00Z",
    author: "Randy",
  },
  {
    id: "fr-002",
    title: "Multi-payment split transactions",
    description: "Support splitting a single transaction across multiple payment methods (cash + card)",
    status: "open",
    priority: "medium",
    createdAt: "2026-06-12T20:30:00Z",
    author: "Randy",
  },
  {
    id: "fr-003",
    title: "Receipt email/SMS delivery",
    description: "Allow customers to receive digital receipts via email or text instead of print",
    status: "open",
    priority: "medium",
    createdAt: "2026-06-12T21:00:00Z",
    author: "Randy",
  },
];

let bugReports: BugReport[] = [
  {
    id: "bug-001",
    title: "Cart total doesn't update on item removal",
    description: "When removing an item from the cart, the total price stays the same until page refresh",
    status: "open",
    priority: "high",
    severity: "high",
    createdAt: "2026-06-12T21:20:00Z",
    author: "Randy",
    stepsToReproduce: "1. Add 3+ items to cart\n2. Remove the second item\n3. Total still shows amount with all 3 items",
  },
];

// --- Versions ---

export function getVersions(): Version[] {
  return [...versions].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function getVersion(id: string): Version | undefined {
  return versions.find((v) => v.id === id);
}

export function getLatestVersionByStage(stage: Stage): Version | undefined {
  return versions
    .filter((v) => v.stage === stage && v.status === "deployed")
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0];
}

export function createVersion(data: Omit<Version, "id">): Version {
  const version: Version = { ...data, id: `v-${String(versions.length + 1).padStart(3, "0")}` };
  versions = [version, ...versions];
  return version;
}

export function promoteVersion(id: string, toStage: Stage): Version | undefined {
  const existing = versions.find((v) => v.id === id);
  if (!existing) return undefined;

  const promoted: Version = {
    ...existing,
    id: `v-${String(versions.length + 1).padStart(3, "0")}`,
    stage: toStage,
    status: "deployed",
    timestamp: new Date().toISOString(),
  };
  versions = [promoted, ...versions];
  return promoted;
}

export function rollbackVersion(id: string): Version | undefined {
  const idx = versions.findIndex((v) => v.id === id);
  if (idx === -1) return undefined;
  versions[idx] = { ...versions[idx], status: "rolled-back" };
  return versions[idx];
}

export function linkFeatureToVersion(versionId: string, featureId: string): void {
  const version = versions.find((v) => v.id === versionId);
  if (version && !version.linkedFeatures.includes(featureId)) {
    version.linkedFeatures.push(featureId);
  }
  const feature = featureRequests.find((f) => f.id === featureId);
  if (feature) {
    feature.completedInVersion = versionId;
    feature.status = "completed";
  }
}

export function linkBugToVersion(versionId: string, bugId: string): void {
  const version = versions.find((v) => v.id === versionId);
  if (version && !version.linkedBugs.includes(bugId)) {
    version.linkedBugs.push(bugId);
  }
  const bug = bugReports.find((b) => b.id === bugId);
  if (bug) {
    bug.fixedInVersion = versionId;
    bug.status = "completed";
  }
}

// --- Feature Requests ---

export function getFeatureRequests(): FeatureRequest[] {
  return [...featureRequests].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getFeatureRequest(id: string): FeatureRequest | undefined {
  return featureRequests.find((f) => f.id === id);
}

export function createFeatureRequest(data: Omit<FeatureRequest, "id" | "createdAt">): FeatureRequest {
  const feature: FeatureRequest = {
    ...data,
    id: `fr-${String(featureRequests.length + 1).padStart(3, "0")}`,
    createdAt: new Date().toISOString(),
  };
  featureRequests = [feature, ...featureRequests];
  return feature;
}

export function updateFeatureRequest(id: string, data: Partial<FeatureRequest>): FeatureRequest | undefined {
  const idx = featureRequests.findIndex((f) => f.id === id);
  if (idx === -1) return undefined;
  featureRequests[idx] = { ...featureRequests[idx], ...data };
  return featureRequests[idx];
}

// --- Bug Reports ---

export function getBugReports(): BugReport[] {
  return [...bugReports].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getBugReport(id: string): BugReport | undefined {
  return bugReports.find((b) => b.id === id);
}

export function createBugReport(data: Omit<BugReport, "id" | "createdAt">): BugReport {
  const bug: BugReport = {
    ...data,
    id: `bug-${String(bugReports.length + 1).padStart(3, "0")}`,
    createdAt: new Date().toISOString(),
  };
  bugReports = [bug, ...bugReports];
  return bug;
}

export function updateBugReport(id: string, data: Partial<BugReport>): BugReport | undefined {
  const idx = bugReports.findIndex((b) => b.id === id);
  if (idx === -1) return undefined;
  bugReports[idx] = { ...bugReports[idx], ...data };
  return bugReports[idx];
}
